import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditIcon from '@material-ui/icons/Edit'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import SaveIcon from '@material-ui/icons/Save'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ZoomInIcon from '@material-ui/icons/ZoomIn'
import ZoomOutIcon from '@material-ui/icons/ZoomOut'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import {
  deleteNewsletterDraft,
  fetchScopedCentres,
  fetchNewsletterDraft,
  fetchNewsletterDrafts,
  freezeNewsletterDraft,
  saveNewsletterDraft,
  updateNewsletterDraft
} from 'api'
import {
  ListPagination,
  usePagedRows
} from 'app/components/generic/ListPagination'
import {
  SearchHeadCell,
  useColumnSearch
} from 'app/components/generic/SearchHeadCell'
import constants from 'app/constants'
import { showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'
import bandBottom from 'assets/images/newsletter/band_bottom.png'
import bandTop from 'assets/images/newsletter/band_top.png'
import ohLogo from 'assets/images/newsletter/oh_logo.svg'
import stripLeft from 'assets/images/newsletter/strip_left.png'
import stripRight from 'assets/images/newsletter/strip_right.png'

import { BORDER_STYLES, borderStyleByKey } from './borders'

// Persist the picked centre across visits (mirrors ParentOnboarding).
const CENTRE_KEY = 'newsletter.centre'

// The list's default centre filter: everything. The API takes this literal
// string ("centre=all"), so it doubles as the Select's value.
const ALL_CENTRES = 'all'

// How large the READ-ONLY view may open the page. Without a cap a wide screen
// renders the 1080px board at ~100% and the reader opens a newsletter to a wall
// of masthead. 0.3 shows the page whole and centred, the way a document viewer
// opens a PDF; zoom takes it from there.
const VIEW_MAX = 0.3

// "ohey times" artboard geometry — taken from the Figma master
// (Openhouse-Master-I → Newsletter page, frame 7318:10). All preview styles
// are in artboard pixels; the pane scales the whole board down visually and
// the PDF is captured at natural size, so both stay true to the design.
const BOARD_W = 1080
const CREAM = '#fbf5ed'
const ORANGE = '#ef5d34'

// PDF export ceilings. Two of them, and a long newsletter hits both.
// Chrome refuses to allocate a canvas past ~65,535px on a side, and does it
// SILENTLY — html2canvas returns an element with no backing store, so
// toDataURL yields an empty string and the PDF embeds a 0x0 image. The
// download looks like it worked and opens blank. Separately a jsPDF page
// tops out at 14400 units (19200px); past that the format clamps and
// everything below the cut is lost. The export scales itself to stay inside
// both. Boards under ~19,200px — nearly every issue — come out byte-identical
// to before: still 2x, still a full-size page.
const PDF_CAPTURE_SCALE = 2
const CANVAS_MAX_SIDE = 60000
const CANVAS_MAX_PIXELS = 90e6
const PDF_MAX_PAGE_PX = 19200

const GRADE_GROUPS = ['Toddlers', 'Pre-Nursery', 'Nursery', 'K1', 'K2']

// Movable segments, in default order. The masthead is always first and the
// thank-you always last; everything between can be rearranged or extended
// with custom segments (keys "custom-<id>").
const DEFAULT_SECTION_ORDER = ['settling', 'team', 'highlights', 'next', 'home']

const blankMember = () => ({ photo: '', name: '', role: '', bio: '' })
const blankGroup = () => ({
  group: GRADE_GROUPS[0],
  description: '',
  photos: []
})
const blankActivity = () => ({ title: '', description: '' })
const blankCustomSection = (id) => ({
  id,
  heading: '',
  body: '',
  photos: []
})

const DEFAULT_THANK_YOU =
  'A big thank you to all our amazing families for your continued trust and ' +
  'support. Together, we’re building a pioneer school in play-based ' +
  'learning, and your encouragement helps create the magic of Openhouse.'
const DEFAULT_FEEDBACK_LINE =
  'Openhouse is always open to your feedback—it helps us create an even ' +
  'better, more joyful experience for your little ones!'

// "13/07/2026, 14:10" — no seconds. A roster of drafts is not a stopwatch; the
// second-precision timestamps just made the column noisy and wide.
const lastUpdated = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—'

// What each list column shows, for the search boxes in its heading.
const DRAFT_SEARCH = {
  name: (d) => d.name,
  centre: (d) => d.centre_name,
  type: (d) => d.comm_type || 'newsletter',
  updated: (d) => lastUpdated(d.modified),
  by: (d) => d.updated_by_name
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    height: 'calc(100vh - 64px)',
    boxSizing: 'border-box'
  },
  listRoot: { padding: theme.spacing(3) },
  // View mode drops the form column, so the board gets the full width.
  viewRoot: { flexDirection: 'column' },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  empty: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  formColumn: {
    width: 430,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  form: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(1)
  },
  formSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5)
  },
  row: { display: 'flex', gap: theme.spacing(1), alignItems: 'center' },
  field: { marginBottom: theme.spacing(1.5) },
  repeatable: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  photoThumb: {
    width: 56,
    height: 56,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`
  },
  previewPane: {
    flex: 1,
    height: '100%',
    overflow: 'auto',
    backgroundColor: theme.palette.grey[200],
    borderRadius: 8,
    position: 'relative'
  },
  previewScaler: {
    transformOrigin: 'top left'
  },
  // Off-screen render target for download-from-list: mounted, laid out and
  // painted (so html2canvas can read it), but never visible. Not display:none,
  // which would give it no dimensions to capture.
  hiddenExport: {
    position: 'fixed',
    top: 0,
    left: -99999,
    zIndex: -1,
    pointerEvents: 'none'
  },
  // The ▲▼ reorder buttons floated over each movable form section.
  moveControls: {
    position: 'absolute',
    top: 6,
    right: 6,
    zIndex: 1,
    display: 'flex'
  },

  /* ------------------------- artboard (preview) ------------------------- */
  // Background bands + card frame come from the picked border style (inline
  // overrides); the classic PNG set is applied in the render for 'classic'.
  // Everything on the board is the brand rounded Avenir per design feedback
  // (was AbhayaLibre serif).
  board: {
    width: BOARD_W,
    backgroundColor: CREAM,
    fontFamily: 'AvenirRounded, Avenir, sans-serif',
    color: '#000',
    boxSizing: 'border-box',
    // Bottom padding must clear the 150px border bands or the last card sits
    // inside the border (same fix as the Event Creative board).
    padding: '170px 90px 175px'
  },
  card: {
    backgroundColor: '#fff',
    padding: '56px 52px 64px',
    boxSizing: 'border-box'
  },
  logo: { width: 336, display: 'block' },
  mastTitle: {
    fontSize: 94,
    fontWeight: 500,
    textTransform: 'lowercase',
    letterSpacing: '-3.8px',
    lineHeight: 1.15,
    margin: '36px 0 0'
  },
  tagline: {
    fontSize: 44,
    fontWeight: 500,
    margin: '18px 0 0 10px',
    lineHeight: 1.2
  },
  // Left margin matches the tagline/quote lines so the month lines up with
  // the rest of the masthead stack.
  issueDate: { fontSize: 30, margin: '22px 0 0 10px' },
  quote: {
    fontSize: 28,
    lineHeight: '38px',
    margin: '26px 0 0 10px',
    whiteSpace: 'pre-wrap'
  },
  quoteAuthor: { fontSize: 28, lineHeight: '38px', margin: '18px 0 0 10px' },
  heading: {
    fontSize: 44,
    fontWeight: 500,
    textTransform: 'lowercase',
    lineHeight: 1.2,
    margin: '0 0 24px'
  },
  subHeading: {
    fontSize: 30,
    fontWeight: 600,
    lineHeight: '40px',
    margin: '0 0 4px'
  },
  body: { fontSize: 28, lineHeight: '38px', margin: 0, whiteSpace: 'pre-wrap' },
  divider: {
    border: 'none',
    borderTop: '3px dotted #b9b2a7',
    margin: '44px 0'
  },
  photo: {
    display: 'block',
    margin: '32px auto',
    maxWidth: 620,
    maxHeight: 640,
    borderRadius: 8
  },
  memberPhoto: {
    width: 260,
    height: 260,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    margin: '8px 0 28px'
  }
}))

// Read a picked file into a data URL, downscaling to keep the preview, the
// PDF and the saved draft payload light (phone photos are 3-12MB originals).
const MAX_PHOTO_DIM = 1400

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const img = new Image()
      img.onload = () => {
        const ratio = Math.min(
          1,
          MAX_PHOTO_DIM / Math.max(img.width, img.height)
        )
        if (ratio === 1 && file.size < 400 * 1024) {
          resolve(reader.result)
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * ratio)
        canvas.height = Math.round(img.height * ratio)
        canvas
          .getContext('2d')
          .drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', 0.85))
      }
      img.onerror = reject
      img.src = reader.result
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const PhotoPicker = ({ label, multiple, onPick }) => {
  const inputRef = useRef(null)
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PhotoCameraIcon />}
        onClick={() => inputRef.current && inputRef.current.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={!!multiple}
        hidden
        onChange={async (e) => {
          const files = Array.from(e.target.files || [])
          if (!files.length) return
          try {
            const urls = await Promise.all(files.map(readFile))
            onPick(urls)
          } catch (err) {
            showToast('Could not read the image', constants.ERROR)
          }
          e.target.value = ''
        }}
      />
    </>
  )
}

const Newsletter = () => {
  const classes = useStyles()

  const [centres, setCentres] = useState([])
  // False once the user is mapped to a centre — "all centres" then means nothing
  // to them, so the option is not offered.
  const [unrestricted, setUnrestricted] = useState(true)
  // Two different things, previously conflated in one state:
  //   centreUuid — the centre THIS newsletter belongs to (the builder's picker,
  //                and what it saves against). A draft must have exactly one.
  //   listCentre — what the LIST is filtered by. Defaults to every centre, so
  //                the page opens on all the newsletters that exist, and you
  //                narrow down from there.
  const [centreUuid, setCentreUuid] = useState(
    () => window.localStorage.getItem(CENTRE_KEY) || ''
  )
  // listCentre is shared by every Centre Operations page and kept across
  // refreshes; centreUuid stays this page's own.
  const [listCentre, setListCentre, pickListCentre] = useOpsCentre({
    fallback: ALL_CENTRES
  })

  const [issueLabel, setIssueLabel] = useState('')
  const [tagline, setTagline] = useState('')
  const [quoteText, setQuoteText] = useState('')
  const [quoteAuthor, setQuoteAuthor] = useState('')
  const [coverPhoto, setCoverPhoto] = useState('')

  const [settlingOn, setSettlingOn] = useState(false)
  const [settlingBody, setSettlingBody] = useState('')

  const [teamOn, setTeamOn] = useState(false)
  const [team, setTeam] = useState([blankMember()])

  const [highlightsTitle, setHighlightsTitle] = useState(
    'highlights of the month'
  )
  const [highlights, setHighlights] = useState([blankGroup()])

  const [nextTitle, setNextTitle] = useState('what’s next?')
  const [nextTheme, setNextTheme] = useState('')
  const [nextBody, setNextBody] = useState('')
  const [nextPhotos, setNextPhotos] = useState([])

  const [homeIntro, setHomeIntro] = useState('')
  const [homeActivities, setHomeActivities] = useState([blankActivity()])

  const [thankYou, setThankYou] = useState(DEFAULT_THANK_YOU)
  const [feedbackLine, setFeedbackLine] = useState(DEFAULT_FEEDBACK_LINE)

  const [borderStyle, setBorderStyle] = useState('classic')
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER)
  const [customSections, setCustomSections] = useState([])

  const [downloading, setDownloading] = useState(false)

  // 'list' shows the saved newsletters for the centre (like Announcements);
  // 'builder' is the form + live preview for one newsletter.
  const [view, setView] = useState('list')
  // Builder opened read-only from the list's View button: same preview, no form.
  const [previewOnly, setPreviewOnly] = useState(false)
  const [drafts, setDrafts] = useState([])
  const [draftUuid, setDraftUuid] = useState('')
  const [savingDraft, setSavingDraft] = useState(false)
  const [loadingDraft, setLoadingDraft] = useState(false)
  // Set when Download is clicked on a list row: the draft loads into the
  // builder, and once the preview has painted we capture the PDF from it.
  const [autoDownload, setAutoDownload] = useState(false)

  const boardRef = useRef(null)
  const paneRef = useRef(null)
  const scalerRef = useRef(null)
  const [scale, setScale] = useState(0.5)
  // The scale that fits the page in the pane, and the reader's manual override
  // (null = follow the fit). The ref lets the resize/mutation handler read the
  // current zoom without re-subscribing on every keystroke.
  const [fitScale, setFitScale] = useState(0.5)
  const [zoom, setZoom] = useState(null)
  const zoomRef = useRef(null)
  useEffect(() => {
    zoomRef.current = zoom
  }, [zoom])

  const ZOOM_MIN = 0.2
  const ZOOM_MAX = 2
  const zoomBy = (factor) =>
    setZoom((z) =>
      Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, (z || fitScale) * factor))
    )

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const data = await fetchScopedCentres()
        const list = data?.results || data || []
        if (!active) return
        setCentres(list)
        setUnrestricted(Boolean(data?.unrestricted))
        setCentreUuid((prev) =>
          prev && list.some((c) => c.uuid === prev) ? prev : list[0]?.uuid || ''
        )
        // A saved centre this person is not mapped to shows every centre.
        setListCentre((prev) =>
          prev === ALL_CENTRES || list.some((c) => c.uuid === prev)
            ? prev
            : ALL_CENTRES
        )
      } catch (e) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (centreUuid) window.localStorage.setItem(CENTRE_KEY, centreUuid)
  }, [centreUuid])

  // Fit the 1080px artboard to the preview pane; keep the pane's scroll
  // height in sync with the scaled board (transform doesn't affect layout).
  //
  // VIEW mode reads like a document viewer: the page at a comfortable width,
  // centred, scrolling down. Two wrong answers were tried first —
  //   * fit to WIDTH: the pane is ~1250px and the board is 1080, so the reader
  //     landed at ~100% and saw a giant masthead ("too zoomed").
  //   * fit the whole PAGE: a newsletter is ONE very long page (~13,000px), so
  //     fitting its height gave 8% — a thumbnail nobody can read.
  // So: fit width, but never blow the page up past VIEW_MAX. Zoom takes it from
  // there. The editor still fits width uncapped — filling its column is exactly
  // what you want while typing.
  useLayoutEffect(() => {
    const fit = () => {
      const pane = paneRef.current
      const board = boardRef.current
      const scaler = scalerRef.current
      if (!pane || !board || !scaler) return
      const byWidth = (pane.clientWidth - 32) / BOARD_W
      const fitted = previewOnly
        ? Math.min(VIEW_MAX, byWidth)
        : Math.min(1, byWidth)
      setFitScale(fitted)
      // A manual zoom wins until the reader asks to fit again.
      const s = zoomRef.current || fitted
      setScale(s)
      scaler.style.height = `${board.offsetHeight * s + 32}px`
    }
    fit()
    window.addEventListener('resize', fit)
    const observer = new MutationObserver(fit)
    if (boardRef.current) {
      observer.observe(boardRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      })
    }
    return () => {
      window.removeEventListener('resize', fit)
      observer.disconnect()
    }
  }, [view, previewOnly, zoom])

  const centre = centres.find((c) => c.uuid === centreUuid)
  const centreName = centre?.name || centre?.centre_name || ''

  const updateRow = (setter) => (index, patch) =>
    setter((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  const removeRow = (setter) => (index) =>
    setter((rows) => rows.filter((_, i) => i !== index))

  const updateMember = updateRow(setTeam)
  const updateGroup = updateRow(setHighlights)
  const updateActivity = updateRow(setHomeActivities)

  /* -------------------------------- drafts ------------------------------- */

  const loadDrafts = useCallback(async () => {
    if (!listCentre) return
    try {
      const rows = await fetchNewsletterDrafts(listCentre)
      // Event creatives share this store (comm_type='event') but live on
      // their own page — keep this list to newsletters.
      setDrafts(
        (Array.isArray(rows) ? rows : []).filter(
          (d) => (d.comm_type || 'newsletter') !== 'event'
        )
      )
    } catch (e) {
      showToast('Could not load saved drafts', constants.ERROR)
    }
  }, [listCentre])

  useEffect(() => {
    setDraftUuid('')
    loadDrafts()
  }, [loadDrafts])

  // 25 a page (50 / 100 on the switch); a centre change starts at page 1.
  // A search box under each heading narrows the list before it is paged.
  const draftSearch = useColumnSearch(drafts, DRAFT_SEARCH)
  const {
    pageRows: draftPage,
    paginationProps: draftPaging,
    resetPage: resetDraftPage
  } = usePagedRows(draftSearch.rows)
  useEffect(() => {
    resetDraftPage()
  }, [listCentre, draftSearch.search])

  // The entire form state, as stored in NewsletterDraft.data. Photos ride
  // along as data URLs — the backend treats the object as opaque.
  const collectData = () => ({
    version: 2,
    issueLabel,
    tagline,
    borderStyle,
    sectionOrder,
    customSections,
    quoteText,
    quoteAuthor,
    coverPhoto,
    settlingOn,
    settlingBody,
    teamOn,
    team,
    highlightsTitle,
    highlights,
    nextTitle,
    nextTheme,
    nextBody,
    nextPhotos,
    homeIntro,
    homeActivities,
    thankYou,
    feedbackLine
  })

  const applyData = (d) => {
    setIssueLabel(d.issueLabel || '')
    setTagline(d.tagline || '')
    // Retired style keys (doodle/waves/…) resolve to classic so the picker
    // never holds a value that's no longer in its menu.
    setBorderStyle(
      BORDER_STYLES.some((s) => s.key === d.borderStyle)
        ? d.borderStyle
        : 'classic'
    )
    const custom = Array.isArray(d.customSections) ? d.customSections : []
    setCustomSections(custom)
    // v1 drafts have no sectionOrder; also drop stale keys and re-append any
    // custom section the order somehow lost.
    const known = new Set([
      ...DEFAULT_SECTION_ORDER,
      ...custom.map((c) => `custom-${c.id}`)
    ])
    const saved = Array.isArray(d.sectionOrder) ? d.sectionOrder : []
    const order = saved.filter((k) => known.has(k))
    DEFAULT_SECTION_ORDER.forEach((k) => {
      if (!order.includes(k)) order.push(k)
    })
    custom.forEach((c) => {
      if (!order.includes(`custom-${c.id}`)) order.push(`custom-${c.id}`)
    })
    setSectionOrder(order)
    setQuoteText(d.quoteText || '')
    setQuoteAuthor(d.quoteAuthor || '')
    setCoverPhoto(d.coverPhoto || '')
    setSettlingOn(!!d.settlingOn)
    setSettlingBody(d.settlingBody || '')
    setTeamOn(!!d.teamOn)
    setTeam(d.team?.length ? d.team : [blankMember()])
    setHighlightsTitle(d.highlightsTitle || 'highlights of the month')
    setHighlights(d.highlights?.length ? d.highlights : [blankGroup()])
    setNextTitle(d.nextTitle || 'what’s next?')
    setNextTheme(d.nextTheme || '')
    setNextBody(d.nextBody || '')
    setNextPhotos(d.nextPhotos || [])
    setHomeIntro(d.homeIntro || '')
    setHomeActivities(
      d.homeActivities?.length ? d.homeActivities : [blankActivity()]
    )
    setThankYou(d.thankYou ?? DEFAULT_THANK_YOU)
    setFeedbackLine(d.feedbackLine ?? DEFAULT_FEEDBACK_LINE)
  }

  const saveDraft = async () => {
    if (!centreUuid) return
    const name = issueLabel.trim()
    if (!name) {
      showToast('Give the newsletter an issue month first', constants.ERROR)
      return
    }
    setSavingDraft(true)
    try {
      const saved = draftUuid
        ? await updateNewsletterDraft(draftUuid, {
            name,
            data: collectData()
          })
        : await saveNewsletterDraft({
            centre: centreUuid,
            name,
            data: collectData(),
            commType: 'newsletter'
          })
      setDraftUuid(saved.uuid)
      showToast(`Draft "${saved.name}" saved`, constants.SUCCESS)
      loadDrafts()
    } catch (e) {
      showToast('Could not save the draft', constants.ERROR)
    } finally {
      setSavingDraft(false)
    }
  }

  const openNew = () => {
    setDraftUuid('')
    applyData({})
    // A new newsletter needs ONE centre. If the list is showing all of them,
    // there is nothing to inherit — leave it unset and let the builder's centre
    // picker ask (Save is already disabled until it is filled).
    setCentreUuid(listCentre === ALL_CENTRES ? '' : listCentre)
    setPreviewOnly(false)
    setView('builder')
  }

  const openDraft = async (uuid, { download = false, preview = false } = {}) => {
    setDraftUuid(uuid)
    setLoadingDraft(true)
    try {
      const draft = await fetchNewsletterDraft(uuid)
      applyData(draft?.data || {})
      // The draft carries its own centre — the list may be showing all of them,
      // so never assume the filter's value is this newsletter's centre.
      if (draft?.centre_uuid) setCentreUuid(draft.centre_uuid)
      // Download from the list must NOT open the editor. It used to load the
      // draft into the builder, flash the edit view, and capture from there —
      // which read as a glitch, and left you sitting in an editor you never
      // asked for. Stay on the list; the board renders off-screen instead.
      if (download) {
        setAutoDownload(true)
        return
      }
      setPreviewOnly(preview)
      // A fresh open starts fitted — the previous newsletter's zoom is not a
      // preference, just where that reader happened to leave off.
      setZoom(null)
      setView('builder')
    } catch (e) {
      showToast('Could not load the newsletter', constants.ERROR)
    } finally {
      setLoadingDraft(false)
    }
  }

  // ?draft=<uuid> (from Preschool Ops › Monthly Newsletter) opens that
  // newsletter straight away, view only.
  useEffect(() => {
    const linked = new URLSearchParams(window.location.search).get('draft')
    if (linked) openDraft(linked, { preview: true })
  }, [])

  const removeDraft = async (uuid) => {
    const current = drafts.find((d) => d.uuid === uuid)
    if (!window.confirm(`Delete "${current?.name || 'this newsletter'}"?`))
      return
    try {
      await deleteNewsletterDraft(uuid)
      if (uuid === draftUuid) setDraftUuid('')
      showToast('Newsletter deleted', constants.SUCCESS)
      loadDrafts()
    } catch (e) {
      showToast('Could not delete the newsletter', constants.ERROR)
    }
  }

  const freezeDraft = async (uuid) => {
    const current = drafts.find((d) => d.uuid === uuid)
    if (
      !window.confirm(
        `Freeze "${current?.name || 'this newsletter'}"? A frozen newsletter ` +
          'can never be edited or deleted again.'
      )
    )
      return
    try {
      await freezeNewsletterDraft(uuid)
      showToast('Newsletter frozen', constants.SUCCESS)
      loadDrafts()
    } catch (e) {
      showToast('Could not freeze the newsletter', constants.ERROR)
    }
  }

  // The open draft's frozen flag (list rows carry it). The backend refuses
  // edits to frozen drafts anyway — this only drives the read-only UI.
  const openIsFrozen = !!drafts.find((d) => d.uuid === draftUuid)?.frozen

  /* ---------------------------- section order ---------------------------- */

  const moveSection = (key, delta) =>
    setSectionOrder((order) => {
      const i = order.indexOf(key)
      const j = i + delta
      if (i < 0 || j < 0 || j >= order.length) return order
      const next = [...order]
      next[i] = next[j]
      next[j] = key
      return next
    })

  const addCustomSection = () => {
    const id = Date.now()
    setCustomSections((s) => [...s, blankCustomSection(id)])
    setSectionOrder((order) => [...order, `custom-${id}`])
  }

  const updateCustomSection = (id, patch) =>
    setCustomSections((s) =>
      s.map((c) => (c.id === id ? { ...c, ...patch } : c))
    )

  const removeCustomSection = (id) => {
    setCustomSections((s) => s.filter((c) => c.id !== id))
    setSectionOrder((order) => order.filter((k) => k !== `custom-${id}`))
  }

  const downloadPdf = async () => {
    const board = boardRef.current
    const scaler = scalerRef.current
    if (!board || !scaler) return
    setDownloading(true)
    // Capture at natural size: html2canvas honours ancestor transforms, so
    // drop the preview scale for the snapshot and restore it right after.
    const prevTransform = scaler.style.transform
    scaler.style.transform = 'none'
    try {
      // Brand fonts must be fully loaded before rasterising, or html2canvas
      // bakes the fallback font into the PDF (font-display: swap).
      if (document.fonts && document.fonts.ready) await document.fonts.ready
      // Pick the largest scale the browser can actually allocate for THIS
      // board rather than always asking for 2x (see the ceilings above).
      const boardH = board.offsetHeight
      const captureScale = Math.min(
        PDF_CAPTURE_SCALE,
        CANVAS_MAX_SIDE / boardH,
        Math.sqrt(CANVAS_MAX_PIXELS / (BOARD_W * boardH))
      )
      const canvas = await html2canvas(board, {
        scale: captureScale,
        backgroundColor: CREAM,
        logging: false
      })
      const image = canvas.toDataURL('image/jpeg', 0.92)
      // An over-large canvas fails without throwing, so check the result
      // instead of trusting it — otherwise we save a blank PDF and say nothing.
      if (!image || image.length < 1000) {
        throw new Error('html2canvas produced an empty canvas')
      }
      // Measure the page off the canvas so its proportions match the image
      // exactly, then shrink it if the board is longer than a page may be.
      const w = canvas.width / captureScale
      const h = canvas.height / captureScale
      const pageScale = Math.min(1, PDF_MAX_PAGE_PX / h)
      const pageW = w * pageScale
      const pageH = h * pageScale
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'px',
        format: [pageW, pageH],
        hotfixes: ['px_scaling'],
        compress: true
      })
      pdf.addImage(image, 'JPEG', 0, 0, pageW, pageH)
      const name = ['Ohey Times', centreName, issueLabel]
        .filter(Boolean)
        .join(' - ')
      pdf.save(`${name || 'Ohey Times'}.pdf`)
    } catch (e) {
      showToast('Could not generate the PDF', constants.ERROR)
    } finally {
      scaler.style.transform = prevTransform
      setDownloading(false)
    }
  }

  // Download-from-list: the board is mounted OFF-SCREEN (see the hidden export
  // stack in the list view) rather than in the editor, so the download happens
  // where you are. Give it a moment to paint — photos are data URLs, so decode
  // is quick — then capture from it.
  useEffect(() => {
    if (!autoDownload) return undefined
    const t = setTimeout(async () => {
      try {
        await downloadPdf()
      } finally {
        setAutoDownload(false)
      }
    }, 800)
    return () => clearTimeout(t)
  }, [autoDownload])

  /* --------------------------------- form -------------------------------- */

  // Each movable segment's form block, keyed for sectionOrder. Custom
  // segments render via renderCustomForm below.
  const sectionForms = {
    settling: () => (
      <Box className={classes.formSection}>
        <FormControlLabel
          control={
            <Checkbox
              checked={settlingOn}
              onChange={(e) => setSettlingOn(e.target.checked)}
              color="primary"
            />
          }
          label={<b>Settling-in &amp; feedback (optional)</b>}
        />
        {settlingOn && (
          <TextField
            label="Body"
            size="small"
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            value={settlingBody}
            onChange={(e) => setSettlingBody(e.target.value)}
          />
        )}
      </Box>
    ),

    team: () => (
      <Box className={classes.formSection}>
        <FormControlLabel
          control={
            <Checkbox
              checked={teamOn}
              onChange={(e) => setTeamOn(e.target.checked)}
              color="primary"
            />
          }
          label={<b>New additions to the team (optional)</b>}
        />
        {teamOn && (
          <>
            {team.map((m, i) => (
              <Box key={i} className={classes.repeatable}>
                <Box className={classes.row} style={{ marginBottom: 8 }}>
                  <PhotoPicker
                    label={m.photo ? 'Replace photo' : 'Photo'}
                    onPick={([url]) => updateMember(i, { photo: url })}
                  />
                  {m.photo && (
                    <img src={m.photo} alt="" className={classes.photoThumb} />
                  )}
                  <Box flex={1} />
                  <IconButton
                    size="small"
                    onClick={() => removeRow(setTeam)(i)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <TextField
                  className={classes.field}
                  label="Name"
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={m.name}
                  onChange={(e) => updateMember(i, { name: e.target.value })}
                />
                <TextField
                  className={classes.field}
                  label="Role (e.g. Early Years Educator)"
                  size="small"
                  variant="outlined"
                  fullWidth
                  value={m.role}
                  onChange={(e) => updateMember(i, { role: e.target.value })}
                />
                <TextField
                  label="Bio"
                  size="small"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  value={m.bio}
                  onChange={(e) => updateMember(i, { bio: e.target.value })}
                />
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setTeam((t) => [...t, blankMember()])}
            >
              Add team member
            </Button>
          </>
        )}
      </Box>
    ),

    highlights: () => (
      <Box className={classes.formSection}>
        <Typography className={classes.sectionTitle}>Highlights</Typography>
        <TextField
          className={classes.field}
          label="Section heading"
          size="small"
          variant="outlined"
          fullWidth
          value={highlightsTitle}
          onChange={(e) => setHighlightsTitle(e.target.value)}
        />
        {highlights.map((g, i) => (
          <Box key={i} className={classes.repeatable}>
            <Box className={classes.row} style={{ marginBottom: 8 }}>
              <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                <InputLabel>Grade group</InputLabel>
                <Select
                  label="Grade group"
                  value={g.group}
                  onChange={(e) => updateGroup(i, { group: e.target.value })}
                >
                  {GRADE_GROUPS.map((gg) => (
                    <MenuItem key={gg} value={gg}>
                      {gg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => removeRow(setHighlights)(i)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              className={classes.field}
              label="What did this group get up to?"
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              value={g.description}
              onChange={(e) => updateGroup(i, { description: e.target.value })}
            />
            <Box className={classes.row} style={{ flexWrap: 'wrap' }}>
              <PhotoPicker
                label="Add photos"
                multiple
                onPick={(urls) =>
                  updateGroup(i, { photos: [...g.photos, ...urls] })
                }
              />
              {g.photos.map((p, pi) => (
                <Box key={pi} position="relative">
                  <img src={p} alt="" className={classes.photoThumb} />
                  <IconButton
                    size="small"
                    onClick={() =>
                      updateGroup(i, {
                        photos: g.photos.filter((_, x) => x !== pi)
                      })
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setHighlights((h) => [...h, blankGroup()])}
        >
          Add grade group
        </Button>
      </Box>
    ),

    next: () => (
      <Box className={classes.formSection}>
        <Typography className={classes.sectionTitle}>
          Next month&apos;s theme
        </Typography>
        <TextField
          className={classes.field}
          label="Section heading (e.g. what’s next in august?)"
          size="small"
          variant="outlined"
          fullWidth
          value={nextTitle}
          onChange={(e) => setNextTitle(e.target.value)}
        />
        <TextField
          className={classes.field}
          label="Theme (e.g. Farm Life)"
          size="small"
          variant="outlined"
          fullWidth
          value={nextTheme}
          onChange={(e) => setNextTheme(e.target.value)}
        />
        <TextField
          className={classes.field}
          label="Description (line breaks are kept — use them for week-by-week plans)"
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={5}
          value={nextBody}
          onChange={(e) => setNextBody(e.target.value)}
        />
        <Box className={classes.row} style={{ flexWrap: 'wrap' }}>
          <PhotoPicker
            label="Add photos"
            multiple
            onPick={(urls) => setNextPhotos((p) => [...p, ...urls])}
          />
          {nextPhotos.map((p, pi) => (
            <Box key={pi} position="relative">
              <img src={p} alt="" className={classes.photoThumb} />
              <IconButton
                size="small"
                onClick={() =>
                  setNextPhotos((arr) => arr.filter((_, x) => x !== pi))
                }
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    ),

    home: () => (
      <Box className={classes.formSection}>
        <Typography className={classes.sectionTitle}>
          Keeping the learning alive at home
        </Typography>
        <TextField
          className={classes.field}
          label="Intro (optional)"
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          value={homeIntro}
          onChange={(e) => setHomeIntro(e.target.value)}
        />
        {homeActivities.map((a, i) => (
          <Box key={i} className={classes.repeatable}>
            <Box className={classes.row} style={{ marginBottom: 8 }}>
              <TextField
                label="Activity title"
                size="small"
                variant="outlined"
                fullWidth
                value={a.title}
                onChange={(e) => updateActivity(i, { title: e.target.value })}
              />
              <IconButton
                size="small"
                onClick={() => removeRow(setHomeActivities)(i)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              label="Description"
              size="small"
              variant="outlined"
              fullWidth
              multiline
              minRows={2}
              value={a.description}
              onChange={(e) =>
                updateActivity(i, { description: e.target.value })
              }
            />
          </Box>
        ))}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setHomeActivities((a) => [...a, blankActivity()])}
        >
          Add activity
        </Button>
      </Box>
    )
  }

  // A user-added segment ("field trip", "events" …) — heading + body + photos.
  const renderCustomForm = (section) => (
    <Box className={classes.formSection}>
      <Box className={classes.row} style={{ marginBottom: 8 }}>
        <Typography className={classes.sectionTitle} style={{ flex: 1 }}>
          {section.heading.trim() || 'Custom section'}
        </Typography>
        <Tooltip title="Remove this section">
          <IconButton
            size="small"
            onClick={() => removeCustomSection(section.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField
        className={classes.field}
        label="Section heading"
        placeholder="e.g. Field trip, Events, Celebrations — add your own"
        size="small"
        variant="outlined"
        fullWidth
        value={section.heading}
        onChange={(e) =>
          updateCustomSection(section.id, { heading: e.target.value })
        }
      />
      <TextField
        className={classes.field}
        label="Body"
        size="small"
        variant="outlined"
        fullWidth
        multiline
        minRows={4}
        value={section.body}
        onChange={(e) =>
          updateCustomSection(section.id, { body: e.target.value })
        }
      />
      <Box className={classes.row} style={{ flexWrap: 'wrap' }}>
        <PhotoPicker
          label="Add photos"
          multiple
          onPick={(urls) =>
            updateCustomSection(section.id, {
              photos: [...section.photos, ...urls]
            })
          }
        />
        {section.photos.map((p, pi) => (
          <Box key={pi} position="relative">
            <img src={p} alt="" className={classes.photoThumb} />
            <IconButton
              size="small"
              onClick={() =>
                updateCustomSection(section.id, {
                  photos: section.photos.filter((_, x) => x !== pi)
                })
              }
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  )

  const renderForm = () => (
    <Box className={classes.form}>
      <Box className={classes.formSection}>
        <Typography className={classes.sectionTitle}>Issue</Typography>
        <FormControl
          variant="outlined"
          size="small"
          fullWidth
          className={classes.field}
        >
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centreUuid}
            onChange={(e) => setCentreUuid(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name || c.centre_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* This field IS the newsletter's name — leaving it blank is what
            produced the pile of "Untitled" rows in the list. Required. */}
        <TextField
          className={classes.field}
          label="Issue month (e.g. August 2025)"
          size="small"
          variant="outlined"
          fullWidth
          required
          error={!issueLabel.trim()}
          helperText={
            !issueLabel.trim()
              ? 'Needed to save — this names the newsletter in the list'
              : ' '
          }
          value={issueLabel}
          onChange={(e) => setIssueLabel(e.target.value)}
        />
        <TextField
          className={classes.field}
          label="Tagline (e.g. In Play Lies the Magic!)"
          size="small"
          variant="outlined"
          fullWidth
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
        />
        <FormControl variant="outlined" size="small" fullWidth>
          <InputLabel>Border style</InputLabel>
          <Select
            label="Border style"
            value={borderStyle}
            onChange={(e) => setBorderStyle(e.target.value)}
          >
            {BORDER_STYLES.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className={classes.formSection}>
        <Typography className={classes.sectionTitle}>Quote</Typography>
        <TextField
          className={classes.field}
          label="Quote"
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          value={quoteText}
          onChange={(e) => setQuoteText(e.target.value)}
        />
        <TextField
          className={classes.field}
          label="Author (e.g. Fred Rogers)"
          size="small"
          variant="outlined"
          fullWidth
          value={quoteAuthor}
          onChange={(e) => setQuoteAuthor(e.target.value)}
        />
        <Box className={classes.row}>
          <PhotoPicker
            label={
              coverPhoto ? 'Replace cover image' : 'Cover image (optional)'
            }
            onPick={([url]) => setCoverPhoto(url)}
          />
          {coverPhoto && (
            <>
              <img src={coverPhoto} alt="" className={classes.photoThumb} />
              <IconButton size="small" onClick={() => setCoverPhoto('')}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {sectionOrder.map((key, index) => {
        const custom = key.startsWith('custom-')
          ? customSections.find((c) => `custom-${c.id}` === key)
          : null
        if (key.startsWith('custom-') && !custom) return null
        return (
          <Box key={key} position="relative">
            <Box className={classes.moveControls}>
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={() => moveSection(key, -1)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={index === sectionOrder.length - 1}
                onClick={() => moveSection(key, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
            {custom ? renderCustomForm(custom) : sectionForms[key]?.()}
          </Box>
        )
      })}

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        style={{ marginBottom: 16 }}
        onClick={addCustomSection}
      >
        Add section (field trip, events…)
      </Button>

      <Box className={classes.formSection}>
        <Typography className={classes.sectionTitle}>Thank you</Typography>
        <TextField
          className={classes.field}
          label="Closing note"
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          value={thankYou}
          onChange={(e) => setThankYou(e.target.value)}
        />
        <TextField
          label="Feedback line"
          size="small"
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          value={feedbackLine}
          onChange={(e) => setFeedbackLine(e.target.value)}
        />
      </Box>
    </Box>
  )

  /* ------------------------------- preview ------------------------------- */

  const activeHighlights = highlights.filter(
    (g) => g.description.trim() || g.photos.length
  )
  const activeTeam = teamOn
    ? team.filter((m) => m.name.trim() || m.bio.trim() || m.photo)
    : []
  const activeActivities = homeActivities.filter(
    (a) => a.title.trim() || a.description.trim()
  )

  const Divider = () => <hr className={classes.divider} />

  // Each movable segment's board block (null when it has nothing to show).
  const sectionBlocks = {
    settling: () =>
      settlingOn && settlingBody.trim() ? (
        <>
          <Divider />
          <p className={classes.heading}>settling-in and feedback</p>
          <p className={classes.body}>{settlingBody}</p>
        </>
      ) : null,

    team: () =>
      activeTeam.length > 0 ? (
        <>
          <Divider />
          <p className={classes.heading}>newest additions to the dream team</p>
          {activeTeam.map((m, i) => (
            <div key={i} style={{ marginBottom: 40 }}>
              {m.photo && (
                <img src={m.photo} alt="" className={classes.memberPhoto} />
              )}
              <p className={classes.subHeading}>
                Meet {m.name}
                {m.role ? `, ${m.role}` : ''}
                {centreName ? `, ${centreName}` : ''}
              </p>
              <p className={classes.body}>{m.bio}</p>
            </div>
          ))}
        </>
      ) : null,

    highlights: () =>
      activeHighlights.length > 0 ? (
        <>
          <Divider />
          <p className={classes.heading}>{highlightsTitle}</p>
          {activeHighlights.map((g, i) => (
            <div key={i} style={{ marginBottom: 36 }}>
              <p className={classes.subHeading}>{g.group}</p>
              <p className={classes.body}>{g.description}</p>
              {g.photos.map((p, pi) => (
                <img key={pi} src={p} alt="" className={classes.photo} />
              ))}
            </div>
          ))}
        </>
      ) : null,

    next: () =>
      nextTheme.trim() || nextBody.trim() || nextPhotos.length > 0 ? (
        <>
          <Divider />
          <p className={classes.heading}>{nextTitle}</p>
          {nextTheme.trim() && (
            <p className={classes.body} style={{ marginBottom: 12 }}>
              This month, we&rsquo;ll begin our new theme&mdash;
              <b>{nextTheme}</b>!
            </p>
          )}
          <p className={classes.body}>{nextBody}</p>
          {nextPhotos.map((p, pi) => (
            <img key={pi} src={p} alt="" className={classes.photo} />
          ))}
        </>
      ) : null,

    home: () =>
      homeIntro.trim() || activeActivities.length > 0 ? (
        <>
          <Divider />
          <p className={classes.heading}>keeping the learning alive at home</p>
          {homeIntro.trim() && (
            <p className={classes.body} style={{ marginBottom: 20 }}>
              {homeIntro}
            </p>
          )}
          {activeActivities.map((a, i) => (
            <div key={i} style={{ marginBottom: 20 }}>
              {a.title.trim() && (
                <p className={classes.subHeading}>{a.title}</p>
              )}
              <p className={classes.body}>{a.description}</p>
            </div>
          ))}
        </>
      ) : null
  }

  const renderCustomBlock = (section) =>
    section.heading.trim() || section.body.trim() || section.photos.length ? (
      <>
        <Divider />
        {section.heading.trim() && (
          <p className={classes.heading}>{section.heading.toLowerCase()}</p>
        )}
        {section.body.trim() && <p className={classes.body}>{section.body}</p>}
        {section.photos.map((p, pi) => (
          <img key={pi} src={p} alt="" className={classes.photo} />
        ))}
      </>
    ) : null

  const renderPreview = () => {
    const style = borderStyleByKey(borderStyle)
    const boardBackground = style.bands
      ? {
          backgroundImage: `${style.bands.top}, ${style.bands.bottom}, ${style.bands.side}, ${style.bands.side}`,
          backgroundPosition: 'left top, left bottom, left top, right top',
          backgroundRepeat: 'repeat-x, repeat-x, repeat-y, repeat-y'
        }
      : {
          backgroundImage: `url(${bandTop}), url(${bandBottom}), url(${stripLeft}), url(${stripRight})`,
          backgroundPosition: 'left top, left bottom, left top, right top',
          backgroundRepeat: 'no-repeat, no-repeat, repeat-y, repeat-y'
        }
    return (
      <div ref={boardRef} className={classes.board} style={boardBackground}>
        <div className={classes.card} style={style.card}>
          <img src={ohLogo} alt="openhouse" className={classes.logo} />
          <p className={classes.mastTitle}>
            ohey times {centreName ? centreName.toLowerCase() : '…'}
          </p>
          {tagline && <p className={classes.tagline}>{tagline}</p>}
          {issueLabel && <p className={classes.issueDate}>{issueLabel}</p>}
          {quoteText && (
            <p className={classes.quote}>&ldquo;{quoteText}&rdquo;</p>
          )}
          {quoteAuthor && <p className={classes.quoteAuthor}>-{quoteAuthor}</p>}
          {coverPhoto && (
            <img src={coverPhoto} alt="" className={classes.photo} />
          )}

          {sectionOrder.map((key) => {
            const custom = key.startsWith('custom-')
              ? customSections.find((c) => `custom-${c.id}` === key)
              : null
            return (
              <React.Fragment key={key}>
                {custom ? renderCustomBlock(custom) : sectionBlocks[key]?.()}
              </React.Fragment>
            )
          })}

          <Divider />
          <p className={classes.heading}>thank you!</p>
          <p className={classes.body}>{thankYou}</p>
          {feedbackLine.trim() && (
            <p className={classes.body} style={{ marginTop: 30 }}>
              {feedbackLine}
            </p>
          )}
        </div>
      </div>
    )
  }

  if (view === 'list') {
    return (
      <Box className={classes.listRoot}>
        <Box className={classes.listHeader}>
          <Typography variant="h5">Newsletters</Typography>
          <Box display="flex" alignItems="center" gridGap={12}>
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: 220 }}
            >
              <InputLabel>Centre</InputLabel>
              <Select
                label="Centre"
                value={listCentre}
                onChange={(e) => pickListCentre(e.target.value)}
              >
                {unrestricted && (
                  <MenuItem value={ALL_CENTRES}>All centres</MenuItem>
                )}
                {centres.map((c) => (
                  <MenuItem key={c.uuid} value={c.uuid}>
                    {c.name || c.centre_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={openNew}
            >
              New Newsletter
            </Button>
          </Box>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SearchHeadCell
                label="Newsletter"
                search={draftSearch.box('name')}
              />
              <SearchHeadCell
                label="Centre"
                search={draftSearch.box('centre')}
              />
              <SearchHeadCell label="Type" search={draftSearch.box('type')} />
              <SearchHeadCell
                label="Last updated"
                search={draftSearch.box('updated')}
              />
              <SearchHeadCell
                label="Updated by"
                search={draftSearch.box('by')}
              />
              <SearchHeadCell label="Actions" align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {draftPage.map((d) => (
              <TableRow key={d.uuid} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gridGap={6}>
                    {d.frozen && (
                      <Tooltip title="Frozen — can no longer be edited or deleted">
                        <LockIcon fontSize="small" color="disabled" />
                      </Tooltip>
                    )}
                    {d.name}
                  </Box>
                </TableCell>
                <TableCell>{d.centre_name || '—'}</TableCell>
                <TableCell style={{ textTransform: 'capitalize' }}>
                  {d.comm_type || 'newsletter'}
                </TableCell>
                <TableCell>
                  {lastUpdated(d.modified)}
                </TableCell>
                <TableCell>{d.updated_by_name || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <span>
                      <IconButton
                        size="small"
                        disabled={loadingDraft}
                        onClick={() => openDraft(d.uuid, { preview: true })}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!d.frozen && (
                    <Tooltip title="Edit">
                      <span>
                        <IconButton
                          size="small"
                          disabled={loadingDraft}
                          onClick={() => openDraft(d.uuid)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Download PDF">
                    <span>
                      <IconButton
                        size="small"
                        disabled={loadingDraft || downloading}
                        onClick={() => openDraft(d.uuid, { download: true })}
                      >
                        <CloudDownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!d.frozen && (
                    <Tooltip title="Freeze (locks this newsletter for good)">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => freezeDraft(d.uuid)}
                        >
                          <LockOpenIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={d.frozen ? 'Frozen — cannot delete' : 'Delete'}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!!d.frozen}
                        onClick={() => removeDraft(d.uuid)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {drafts.length > 0 && !draftSearch.rows.length && (
              <TableRow>
                <TableCell colSpan={6} className={classes.empty}>
                  No newsletters match the search.
                </TableCell>
              </TableRow>
            )}
            {!drafts.length && (
              <TableRow>
                <TableCell colSpan={6} className={classes.empty}>
                  {listCentre === ALL_CENTRES
                    ? 'No newsletters yet — click "New Newsletter" to create the first one.'
                    : 'No newsletters yet for this centre — click "New Newsletter" to create the first one.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ListPagination {...draftPaging} />

        {/* Download-from-list renders the board HERE, off-screen, and captures
            it. Previously it opened the builder and grabbed the board from
            there, so clicking Download bounced you into an editor you never
            asked for and then downloaded — which looked broken. */}
        {autoDownload ? (
          <div className={classes.hiddenExport} aria-hidden>
            <div ref={scalerRef} className={classes.previewScaler}>
              {renderPreview()}
            </div>
          </div>
        ) : null}
      </Box>
    )
  }

  const openName = draftUuid
    ? drafts.find((d) => d.uuid === draftUuid)?.name || 'Newsletter'
    : 'New newsletter'

  // View mode: the board on its own, with Back and Download. No form, no save.
  if (previewOnly) {
    return (
      <Box className={`${classes.root} ${classes.viewRoot}`}>
        <Box mb={2} display="flex" alignItems="center" gridGap={8} width="100%">
          <Tooltip title="Back to all newsletters">
            <IconButton size="small" onClick={() => setView('list')}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" style={{ flex: 1 }} noWrap>
            {openName}
          </Typography>
          {openIsFrozen && (
            <Tooltip title="This newsletter is frozen — it can no longer be edited or deleted">
              <LockIcon fontSize="small" color="disabled" />
            </Tooltip>
          )}
          <Tooltip title="Zoom out">
            <span>
              <IconButton
                size="small"
                onClick={() => zoomBy(1 / 1.25)}
                disabled={scale <= ZOOM_MIN + 0.001}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={zoom ? 'Fit the whole page' : 'Fitting the whole page'}>
            <span>
              <Button
                size="small"
                onClick={() => setZoom(null)}
                disabled={!zoom}
                style={{ minWidth: 64 }}
              >
                {Math.round(scale * 100)}%
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Zoom in">
            <span>
              <IconButton
                size="small"
                onClick={() => zoomBy(1.25)}
                disabled={scale >= ZOOM_MAX - 0.001}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={
              downloading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CloudDownloadIcon />
              )
            }
            disabled={downloading}
            onClick={downloadPdf}
          >
            Download PDF
          </Button>
        </Box>
        <Box
          className={classes.previewPane}
          ref={paneRef}
          p={2}
          width="100%"
          minHeight={0}
        >
          {/* Centred like a document viewer — a page pinned to the left edge of
              a wide grey pane reads as broken. The scaler keeps its top-left
              origin (the height maths depends on it), so the centring is done
              by the wrapper's margin, not the transform. */}
          <div
            style={{
              width: BOARD_W * scale,
              margin: '0 auto',
              maxWidth: '100%'
            }}
          >
            <div
              ref={scalerRef}
              className={classes.previewScaler}
              style={{ transform: `scale(${scale})` }}
            >
              {renderPreview()}
            </div>
          </div>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.formColumn}>
        <Box mb={2} display="flex" alignItems="center" gridGap={8}>
          <Tooltip title="Back to all newsletters">
            <IconButton size="small" onClick={() => setView('list')}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" style={{ flex: 1 }} noWrap>
            {openName}
          </Typography>
          {openIsFrozen && (
            <Tooltip title="This newsletter is frozen — it can no longer be edited or deleted">
              <LockIcon fontSize="small" color="disabled" />
            </Tooltip>
          )}
          <Tooltip title="Save this newsletter to the server (named after the Issue month field)">
            <span>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={
                  savingDraft ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={
                  savingDraft ||
                  !centreUuid ||
                  !issueLabel.trim() ||
                  openIsFrozen
                }
                onClick={saveDraft}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </Box>
        {renderForm()}
      </Box>
      <Box className={classes.previewPane} ref={paneRef} p={2}>
        <div
          ref={scalerRef}
          className={classes.previewScaler}
          style={{ transform: `scale(${scale})` }}
        >
          {renderPreview()}
        </div>
      </Box>
    </Box>
  )
}

export default Newsletter
